import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import {
  Key,
  Plus,
  Loader2,
  AlertTriangle,
  Clock,
  RefreshCw,
  Shield,
  ShieldCheck,
  Copy,
  Lock,
  Unlock,
} from "lucide-react";
import { format } from "date-fns";
import {
  useMasterKeys,
  useRecoveryCodes,
  useCreateRecoveryCode,
  useUseRecoveryCode,
  MasterKey,
  RecoveryCode,
} from "@/hooks/useEncryptionPolicies";
import { hashKey } from "@/hooks/useEncryptionKeyManagement";

const STATUS_COLORS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  active: "default",
  used: "outline",
  expired: "secondary",
  revoked: "destructive",
};

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  used: "Used",
  expired: "Expired",
  revoked: "Revoked",
};

function RecoveryCodesList({
  masterKey,
  onRefresh,
}: {
  masterKey: MasterKey;
  onRefresh: () => void;
}) {
  const { data: codes, isLoading, refetch } = useRecoveryCodes(masterKey.id);
  const createMutation = useCreateRecoveryCode();
  const useMutation = useUseRecoveryCode();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [useDialogOpen, setUseDialogOpen] = useState(false);
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [recoveryCodeInput, setRecoveryCodeInput] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const activeCodes = codes?.filter((c) => c.status === "active") ?? [];

  const generateSecureCode = () => {
    const array = new Uint8Array(24);
    crypto.getRandomValues(array);
    return Array.from(array)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
  };

  const handleCreateCode = async () => {
    const code = generateSecureCode();
    const codeHash = await hashKey(code);
    const codeHint = `${code.slice(0, 4)}...${code.slice(-4)}`;

    try {
      await createMutation.mutateAsync({
        master_key_id: masterKey.id,
        code_hash: codeHash,
        code_hint: codeHint,
        expires_at: expiresAt || null,
      });
      setGeneratedCode(code);
      toast.success("Recovery code created successfully");
      refetch();
      onRefresh();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to create recovery code");
    }
  };

  const handleUseCode = async () => {
    if (!recoveryCodeInput) {
      toast.error("Please enter a recovery code");
      return;
    }

    try {
      const codeHash = await hashKey(recoveryCodeInput.trim().toUpperCase());
      const success = await useMutation.mutateAsync({
        master_key_id: masterKey.id,
        code_hash: codeHash,
      });

      if (success) {
        toast.success("Recovery code verified successfully");
        setUseDialogOpen(false);
        setRecoveryCodeInput("");
        refetch();
        onRefresh();
      } else {
        toast.error("Invalid recovery code");
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to verify recovery code");
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-muted-foreground">
          {activeCodes.length} active recovery code{activeCodes.length !== 1 ? "s" : ""}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUseDialogOpen(true)}
            disabled={activeCodes.length === 0}
          >
            <Unlock className="h-4 w-4 mr-1" />
            Use Code
          </Button>
          <Button
            size="sm"
            onClick={() => {
              setGeneratedCode(null);
              setExpiresAt("");
              setCreateDialogOpen(true);
            }}
            disabled={activeCodes.length >= 10}
          >
            <Plus className="h-4 w-4 mr-1" />
            Create Code
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : codes && codes.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Hint</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Expires</TableHead>
              <TableHead>Used</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codes.map((code) => (
              <TableRow key={code.id}>
                <TableCell>
                  <code className="bg-muted px-1.5 py-0.5 rounded text-sm">
                    {code.code_hint || "N/A"}
                  </code>
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_COLORS[code.status]}>
                    {STATUS_LABELS[code.status]}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {format(new Date(code.created_at), "MMM d, yyyy")}
                </TableCell>
                <TableCell className="text-sm">
                  {code.expires_at
                    ? format(new Date(code.expires_at), "MMM d, yyyy")
                    : "Never"}
                </TableCell>
                <TableCell className="text-sm">
                  {code.used_at ? (
                    <span className="text-muted-foreground">
                      {format(new Date(code.used_at), "MMM d, yyyy")}
                      {code.used_by_email && ` by ${code.used_by_email}`}
                    </span>
                  ) : (
                    "-"
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : (
        <div className="text-center py-4 text-muted-foreground text-sm">
          No recovery codes for this key
        </div>
      )}

      {/* Create Code Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Recovery Code</DialogTitle>
            <DialogDescription>
              Generate a new emergency recovery code for "{masterKey.key_alias}"
            </DialogDescription>
          </DialogHeader>

          {generatedCode ? (
            <div className="space-y-4">
              <Alert>
                <ShieldCheck className="h-4 w-4" />
                <AlertTitle>Recovery Code Generated</AlertTitle>
                <AlertDescription>
                  Store this code securely. It will only be shown once!
                </AlertDescription>
              </Alert>

              <div className="relative">
                <Input
                  value={generatedCode}
                  readOnly
                  className="font-mono pr-10"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 h-7 w-7"
                  onClick={() => copyToClipboard(generatedCode)}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>

              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  Write this code down and store it in a secure location. You will not be able to
                  view it again.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="codeExpiry">Expiration Date (Optional)</Label>
                <Input
                  id="codeExpiry"
                  type="datetime-local"
                  value={expiresAt}
                  onChange={(e) => setExpiresAt(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Leave empty for a code that never expires
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              {generatedCode ? "Close" : "Cancel"}
            </Button>
            {!generatedCode && (
              <Button onClick={handleCreateCode} disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Generate Code
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Use Code Dialog */}
      <Dialog open={useDialogOpen} onOpenChange={setUseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Use Recovery Code</DialogTitle>
            <DialogDescription>
              Enter an emergency recovery code for "{masterKey.key_alias}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <Lock className="h-4 w-4" />
              <AlertDescription>
                Using a recovery code is logged for security purposes. The code will be marked as
                used and cannot be reused.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="recoveryCode">Recovery Code</Label>
              <Input
                id="recoveryCode"
                placeholder="Enter your recovery code"
                value={recoveryCodeInput}
                onChange={(e) => setRecoveryCodeInput(e.target.value.toUpperCase())}
                className="font-mono"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUseCode} disabled={useMutation.isPending}>
              {useMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verify Code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function EmergencyKeyRecovery() {
  const { data: masterKeys, isLoading, refetch } = useMasterKeys();

  const activeKeys = masterKeys?.filter((k) => k.status === "active") ?? [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium">Emergency Key Recovery</h3>
          <p className="text-sm text-muted-foreground">
            Manage recovery codes for platform master keys
          </p>
        </div>
        <Button variant="outline" onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Info Alert */}
      <Alert>
        <Shield className="h-4 w-4" />
        <AlertTitle>About Recovery Codes</AlertTitle>
        <AlertDescription>
          Recovery codes provide emergency access to master keys. Each code can only be used once and
          all usage is logged for compliance. Store codes in a secure, offline location.
        </AlertDescription>
      </Alert>

      {activeKeys.length === 0 ? (
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-muted-foreground">
              <Key className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No active master keys</p>
              <p className="text-sm">Create a master key first to add recovery codes</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Recovery Codes by Master Key
            </CardTitle>
            <CardDescription>
              Manage recovery codes for each active master key
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {activeKeys.map((key) => (
                <AccordionItem key={key.id} value={key.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3">
                      <Key className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{key.key_alias}</span>
                      <Badge variant="outline">v{key.key_version}</Badge>
                      {key.has_recovery_codes && (
                        <Badge variant="secondary">
                          {key.recovery_codes_count} code{key.recovery_codes_count !== 1 ? "s" : ""}
                        </Badge>
                      )}
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-4">
                      <RecoveryCodesList masterKey={key} onRefresh={refetch} />
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
